import React, { useState } from 'react';
import { X, CreditCard, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FinancingModal = ({ isOpen, onClose, product }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    term_months: '36',
    message: '',
    privacy_accepted: false
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_accepted) {
      toast.error('Bitte stimmen Sie der Datenverarbeitung zu');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/financing/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          term_months: parseInt(formData.term_months),
          product_name: product.name,
          product_slug: product.slug,
          purchase_price: product.pricing?.numericPrice || 0
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success('Anfrage erfolgreich gesendet!');
      } else {
        throw new Error(data.detail || 'Fehler beim Senden');
      }
    } catch (error) {
      toast.error(error.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setFormData({
      first_name: '',
      last_name: '',
      company: '',
      email: '',
      phone: '',
      term_months: '36',
      message: '',
      privacy_accepted: false
    });
    onClose();
  };

  // Success view
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Anfrage gesendet!</h2>
          <p className="text-gray-600 mb-6">
            Vielen Dank für Ihre Finanzierungsanfrage. Wir prüfen Ihre Angaben und melden uns in Kürze bei Ihnen.
          </p>
          <Button onClick={handleClose} className="w-full bg-red-600 hover:bg-red-700">
            Schließen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8">
        {/* Header */}
        <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold">Finanzierung / Leasing anfragen</h2>
              <p className="text-red-100 text-sm">Schnelle Prüfung, keine Verpflichtung</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-red-700 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Product Info */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <p className="text-sm text-gray-500">Gewünschte Maschine</p>
          <p className="font-bold text-gray-900">{product.name}</p>
          <p className="text-xl font-bold text-red-600">{product.pricing?.buy || 'Preis auf Anfrage'}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vorname *
              </label>
              <Input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder="Max"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nachname *
              </label>
              <Input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder="Mustermann"
              />
            </div>
          </div>

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Firma <span className="text-gray-400">(optional)</span>
            </label>
            <Input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Mustermann GmbH"
            />
          </div>

          {/* Contact Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail *
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="max@beispiel.de"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon *
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="0123 456789"
              />
            </div>
          </div>

          {/* Term Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gewünschte Laufzeit *
            </label>
            <div className="grid grid-cols-5 gap-2">
              {['12', '24', '36', '48', '60'].map((months) => (
                <button
                  key={months}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, term_months: months }))}
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                    formData.term_months === months
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-red-300'
                  }`}
                >
                  {months} Mon.
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nachricht <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Haben Sie besondere Wünsche oder Fragen?"
            />
          </div>

          {/* Privacy Checkbox */}
          <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
            <input
              type="checkbox"
              id="privacy_accepted"
              name="privacy_accepted"
              checked={formData.privacy_accepted}
              onChange={handleChange}
              className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              required
            />
            <label htmlFor="privacy_accepted" className="text-sm text-gray-600">
              Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
              <a href="/datenschutz" target="_blank" className="text-red-600 hover:underline">
                Datenschutzerklärung
              </a>{' '}
              zu. Meine Daten werden nur zur Bearbeitung der Finanzierungsanfrage verwendet. *
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Finanzierungsanfrage senden
              </>
            )}
          </Button>

          <p className="text-xs text-center text-gray-500">
            Ihre Anfrage ist unverbindlich. Wir erstellen Ihnen ein individuelles Angebot.
          </p>
        </form>
      </div>
    </div>
  );
};

export default FinancingModal;
